import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import {
  Card,
  CardContent,
  Box,
  SxProps,
  Theme,
  useTheme,
} from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";

export interface PieChartData {
  label: string;
  orders_count: number;
}

export interface PieChartComponentProps {
  title: string;
  data: PieChartData[];
  sx?: SxProps<Theme>;
  height?: number;
  outerRadius?: number;
  showLegend?: boolean;
  showTooltip?: boolean;
  colors: string[];
  message?: string;
}

const CustomPieTooltip = ({ active, payload, theme, data, colors }: any) => {
  if (active && payload && payload.length && payload[0]) {
    const dataItem = payload[0].payload;
    const total = data.reduce(
      (sum: number, item: PieChartData) => sum + item.orders_count,
      0
    );
    const percentage: number = total > 0 ? (payload[0].value / total) * 100 : 0;

    const segmentColor =
      payload[0].color ||
      (payload[0].payload &&
        colors[
          data.findIndex(
            (item: { label: any }) => item.label === payload[0].payload.label
          )
        ]) ||
      theme.palette.primary.main;

    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          padding: 1,
          boxShadow: theme.shadows[2],
          minWidth: 160,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginBottom: 1,
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              backgroundColor: segmentColor,
              borderRadius: "50%",
              marginRight: 1.5,
            }}
          />
          <CustomTypography variant="heading8" fontWeight={600}>
            {dataItem.label}
          </CustomTypography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <CustomTypography variant="body4">Orders:</CustomTypography>
          <CustomTypography variant="body4">
            {payload[0].value}
          </CustomTypography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <CustomTypography variant="body4">Percentage:</CustomTypography>
          <CustomTypography variant="body4">
            {percentage.toFixed(1)}%
          </CustomTypography>
        </Box>
      </Box>
    );
  }
  return null;
};

export default function PieChartComponent({
  title,
  data,
  sx,
  message,
  height = 300,
  outerRadius = 100,
  showLegend = true,
  showTooltip = true,
  colors,
}: PieChartComponentProps) {
  const theme = useTheme();

  return (
    <Card sx={{ padding: [2, 0, 2, 2], ...sx }}>
      <CardContent>
        <CustomTypography
          variant="heading4"
          color={theme.palette.customColors.blackBorder}
        >
          {title}
        </CustomTypography>

        {data.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: `${height}px`,
            }}
          >
            <CustomTypography variant="heading7">
              No order data available.
            </CustomTypography>
          </Box>
        ) : (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: `${height}px`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box sx={{ width: "100%", height: "70%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    dataKey="orders_count"
                    outerRadius={`${outerRadius}%`}
                    label={({
                      cx,
                      cy,
                      midAngle,
                      innerRadius,
                      outerRadius,
                      percent,
                      index,
                    }) => {
                      const radius =
                        innerRadius + (outerRadius - innerRadius) / 2;
                      const x =
                        cx + radius * Math.cos(-midAngle * (Math.PI / 180));
                      const y =
                        cy + radius * Math.sin(-midAngle * (Math.PI / 180));
                      return (
                        <text
                          x={x}
                          y={y}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontWeight="bold"
                        >
                          <tspan fontSize="0.8rem" fill={theme.palette.customColors.white}>
                            {(percent * 100).toFixed(0)}%
                          </tspan>
                        </text>
                      );
                    }}
                    labelLine={false}
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={colors[index]}
                        name={entry.label} // Ensure name is passed for tooltip
                      />
                    ))}
                  </Pie>
                  {showTooltip && (
                    <RechartsTooltip
                      content={
                        <CustomPieTooltip
                          theme={theme}
                          data={data}
                          colors={colors}
                        />
                      }
                      cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
                    />
                  )}
                </PieChart>
              </ResponsiveContainer>
            </Box>

            {showLegend && (
              <Box sx={{ width: "100%", height: "30%", marginTop: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  {data.map((entry, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: "12px",
                          height: "12px",
                          backgroundColor: colors[index],
                          borderRadius: "50%",
                        }}
                      />
                      <CustomTypography
                        variant="body3"
                        sx={{
                          fontSize: {
                            xs: "0.75rem", // Mobile
                            sm: "0.875rem", // Tablet
                            md: "1rem", // Desktop
                          },
                        }}
                      >
                        {entry.label} ({entry.orders_count})
                      </CustomTypography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
